import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Paginator } from 'primereact/paginator';
import { Dialog } from 'primereact/dialog';
import { Messages } from 'primereact/messages';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import Container from 'react-bootstrap/Container';
import resourcesApi from '../api/resourcesApi';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const DEFAULT_PAGE_SIZE = 25;

const parseBoolParam = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const AdminResources = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const msgs = useRef(null);
  const toast = useRef(null);
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect();

  const appliedFilters = useMemo(() => {
    const portalScope = searchParams.get('portalScope') || '';
    const productScope = searchParams.get('productScope') || '';
    const category = searchParams.get('category') || '';
    const search = searchParams.get('search') || '';
    const isActiveParam = searchParams.get('isActive');
    const includeDeletedParam = searchParams.get('includeDeleted');

    const page = Math.max(1, Number(searchParams.get('page') || 1));
    const pageSize = Math.max(1, Number(searchParams.get('pageSize') || DEFAULT_PAGE_SIZE));

    return {
      portalScope,
      productScope,
      category,
      search,
      isActive: isActiveParam === null ? '' : String(isActiveParam),
      includeDeleted: includeDeletedParam === null ? false : Boolean(parseBoolParam(includeDeletedParam)),
      page,
      pageSize,
    };
  }, [searchParams]);

  const [uiPortalScope, setUiPortalScope] = useState(appliedFilters.portalScope);
  const [uiProductScope, setUiProductScope] = useState(appliedFilters.productScope);
  const [uiCategory, setUiCategory] = useState(appliedFilters.category);
  const [uiSearch, setUiSearch] = useState(appliedFilters.search);
  const [uiIsActive, setUiIsActive] = useState(appliedFilters.isActive);
  const [uiIncludeDeleted, setUiIncludeDeleted] = useState(appliedFilters.includeDeleted);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [confirmStatusVisible, setConfirmStatusVisible] = useState(false);
  const [confirmActionLoading, setConfirmActionLoading] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [nextStatusValue, setNextStatusValue] = useState(false);

  useEffect(() => {
    setUiPortalScope(appliedFilters.portalScope);
    setUiProductScope(appliedFilters.productScope);
    setUiCategory(appliedFilters.category);
    setUiSearch(appliedFilters.search);
    setUiIsActive(appliedFilters.isActive);
    setUiIncludeDeleted(appliedFilters.includeDeleted);
  }, [
    appliedFilters.portalScope,
    appliedFilters.productScope,
    appliedFilters.category,
    appliedFilters.search,
    appliedFilters.isActive,
    appliedFilters.includeDeleted,
  ]);

  const showError = (error, fallbackMessage) => {
    if (handleAccessError(error)) return;

    const detail =
      (error && error.response && error.response.data) ||
      (error && error.message) ||
      fallbackMessage;

    if (msgs.current) {
      msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
    }
  };

  const loadResources = async () => {
    try {
      setLoading(true);
      clearAccessIssue();
      if (msgs.current) msgs.current.clear();

      const { items: nextItems, total: nextTotal } = await resourcesApi.getAdminResources({
        portalScope: appliedFilters.portalScope || undefined,
        productScope: appliedFilters.productScope || undefined,
        category: appliedFilters.category || undefined,
        isActive: appliedFilters.isActive === '' ? undefined : appliedFilters.isActive,
        includeDeleted: appliedFilters.includeDeleted,
        page: appliedFilters.page,
        pageSize: appliedFilters.pageSize,
        search: appliedFilters.search || undefined,
      });

      setItems(Array.isArray(nextItems) ? nextItems : []);
      setTotal(Number.isFinite(Number(nextTotal)) ? Number(nextTotal) : 0);
    } catch (error) {
      showError(error, 'Failed to load resources.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
  }, [appliedFilters.portalScope, appliedFilters.productScope, appliedFilters.category, appliedFilters.isActive, appliedFilters.includeDeleted, appliedFilters.page, appliedFilters.pageSize, appliedFilters.search]);

  const updateParams = (next) => {
    const params = new URLSearchParams();
    if (next.portalScope) params.set('portalScope', String(next.portalScope));
    if (next.productScope) params.set('productScope', String(next.productScope));
    if (next.category) params.set('category', String(next.category));
    if (next.search) params.set('search', String(next.search));
    if (next.isActive !== undefined && next.isActive !== null && String(next.isActive) !== '') params.set('isActive', String(next.isActive));
    if (next.includeDeleted) params.set('includeDeleted', 'true');
    params.set('page', String(next.page || 1));
    params.set('pageSize', String(next.pageSize || DEFAULT_PAGE_SIZE));

    setSearchParams(params, { replace: true });
  };

  const applyFilters = () => {
    updateParams({
      portalScope: uiPortalScope,
      productScope: uiProductScope,
      category: String(uiCategory || '').trim(),
      search: String(uiSearch || '').trim(),
      isActive: uiIsActive,
      includeDeleted: Boolean(uiIncludeDeleted),
      page: 1,
      pageSize: appliedFilters.pageSize,
    });
  };

  const resetFilters = () => {
    setUiPortalScope('');
    setUiProductScope('');
    setUiCategory('');
    setUiSearch('');
    setUiIsActive('');
    setUiIncludeDeleted(false);
    updateParams({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  };

  const onPageChange = (e) => {
    const nextPageSize = Number(e.rows || appliedFilters.pageSize);
    const nextFirst = Number(e.first || 0);
    const nextPage = Math.floor(nextFirst / nextPageSize) + 1;

    updateParams({
      ...appliedFilters,
      page: nextPage,
      pageSize: nextPageSize,
    });
  };

  const openDeleteConfirm = (row) => {
    setSelectedResource(row);
    setConfirmDeleteVisible(true);
  };

  const openStatusConfirm = (row) => {
    const current = Boolean(row && row.isActive);
    setSelectedResource(row);
    setNextStatusValue(!current);
    setConfirmStatusVisible(true);
  };

  const performDelete = async () => {
    if (!selectedResource) return;

    try {
      setConfirmActionLoading(true);
      await resourcesApi.deleteResource(selectedResource.id);
      setConfirmDeleteVisible(false);
      setSelectedResource(null);
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: 'Resource deleted.' });
      }
      await loadResources();
    } catch (error) {
      showError(error, 'Failed to delete resource.');
    } finally {
      setConfirmActionLoading(false);
    }
  };

  const performStatusChange = async () => {
    if (!selectedResource) return;

    try {
      setConfirmActionLoading(true);
      await resourcesApi.patchResourceStatus(selectedResource.id, nextStatusValue);
      setConfirmStatusVisible(false);
      setSelectedResource(null);
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: '',
          detail: nextStatusValue ? 'Resource enabled.' : 'Resource disabled.',
        });
      }
      await loadResources();
    } catch (error) {
      showError(error, 'Failed to update status.');
    } finally {
      setConfirmActionLoading(false);
    }
  };

  const portalOptions = [
    { label: 'All portals', value: '' },
    { label: 'Support', value: 'support' },
    { label: 'Internal', value: 'internal' },
    { label: 'Both', value: 'both' },
  ];

  const productOptions = [
    { label: 'All products', value: '' },
    { label: 'MyFit', value: 'myfit' },
    { label: 'SmartTile', value: 'smarttile' },
    { label: 'Both', value: 'both' },
  ];

  const isActiveOptions = [
    { label: 'All', value: '' },
    { label: 'Active', value: 'true' },
    { label: 'Inactive', value: 'false' },
  ];

  const updatedAtTemplate = (row) => {
    const raw = row.updatedAt || row.updatedAtUtc || row.modifiedAt || row.modifiedAtUtc;
    if (!raw) return '';
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? String(raw) : date.toLocaleString();
  };

  const activeTemplate = (row) => (
    <span>{Boolean(row.isActive) ? 'Yes' : 'No'}</span>
  );

  const actionsTemplate = (row) => (
    <div className="flex align-items-center gap-2">
      <Button
        label="Edit"
        icon="pi pi-pencil"
        className="p-button-text"
        onClick={() => navigate(`/admin/resources/${row.id}/edit${location.search || ''}`)}
      />
      <Button
        label={Boolean(row.isActive) ? 'Disable' : 'Enable'}
        icon={Boolean(row.isActive) ? 'pi pi-ban' : 'pi pi-check'}
        className="p-button-text"
        onClick={() => openStatusConfirm(row)}
      />
      <Button
        label="Delete"
        icon="pi pi-trash"
        className="p-button-text p-button-danger"
        onClick={() => openDeleteConfirm(row)}
      />
    </div>
  );

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  return (
    <Card className="justify-content-md-center mt-5">
      <Container fluid="md">
        <div className="panel">
          <div className="flex align-content-center flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => navigate('/customers')} className="flex mb-3" />
            <h4 className="flex mt-2 mb-3 ml-3 mr-3">Admin - Resources</h4>
          </div>
        </div>

        <Messages ref={msgs} />
        <Toast ref={toast} />

        <div className="card p-3 mb-3">
          <div className="flex align-items-center flex-wrap gap-3">
            <Dropdown
              value={uiPortalScope}
              options={portalOptions}
              onChange={(e) => setUiPortalScope(e.value)}
              placeholder="Portal scope"
              style={{ minWidth: '200px' }}
            />
            <Dropdown
              value={uiProductScope}
              options={productOptions}
              onChange={(e) => setUiProductScope(e.value)}
              placeholder="Product scope"
              style={{ minWidth: '200px' }}
            />
            <InputText
              value={uiCategory}
              onChange={(e) => setUiCategory(e.target.value)}
              placeholder="Category"
              style={{ minWidth: '180px' }}
            />
            <Dropdown
              value={uiIsActive}
              options={isActiveOptions}
              onChange={(e) => setUiIsActive(e.value)}
              placeholder="Active"
              style={{ minWidth: '160px' }}
            />
            <div className="flex align-items-center gap-2">
              <span>Include deleted</span>
              <InputSwitch checked={uiIncludeDeleted} onChange={(e) => setUiIncludeDeleted(Boolean(e.value))} />
            </div>
            <InputText
              value={uiSearch}
              onChange={(e) => setUiSearch(e.target.value)}
              placeholder="Search"
              style={{ minWidth: '220px' }}
            />
            <Button label="Apply" icon="pi pi-filter" onClick={applyFilters} />
            <Button label="Reset" className="p-button-text" onClick={resetFilters} />
            <div className="flex-grow-1" />
            <Button
              label="Add Resource"
              icon="pi pi-plus"
              className="p-button-success"
              onClick={() => navigate(`/admin/resources/create${location.search || ''}`)}
            />
          </div>
        </div>

        {loading && progressbar}

        <DataTable
          value={items}
          dataKey="id"
          emptyMessage={loading ? 'Loading resources...' : 'No resources found.'}
          scrollable
          scrollHeight="calc(100vh - 360px)"
        >
          <Column field="title" header="Title" style={{ minWidth: '18rem' }} sortable />
          <Column field="category" header="Category" style={{ minWidth: '12rem' }} sortable />
          <Column field="portalScope" header="Portal Scope" style={{ minWidth: '10rem' }} sortable />
          <Column field="productScope" header="Product Scope" style={{ minWidth: '10rem' }} sortable />
          <Column header="Active" body={activeTemplate} style={{ minWidth: '6rem' }} />
          <Column header="Updated" body={updatedAtTemplate} style={{ minWidth: '14rem' }} />
          <Column header="Actions" body={actionsTemplate} style={{ minWidth: '22rem' }} />
        </DataTable>

        <div className="mt-3">
          <Paginator
            first={(appliedFilters.page - 1) * appliedFilters.pageSize}
            rows={appliedFilters.pageSize}
            totalRecords={total}
            rowsPerPageOptions={[10, 20, 25, 50, 75, 100]}
            onPageChange={onPageChange}
          />
        </div>

        <Dialog
          header="Confirm Delete"
          visible={confirmDeleteVisible}
          onHide={() => (confirmActionLoading ? null : setConfirmDeleteVisible(false))}
          modal
          style={{ width: '520px' }}
        >
          <p>
            Delete resource <strong>{selectedResource ? selectedResource.title : ''}</strong>?
          </p>
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmDeleteVisible(false)} disabled={confirmActionLoading} />
            <Button label="Delete" className="p-button-danger" onClick={performDelete} disabled={confirmActionLoading} />
          </div>
        </Dialog>

        <Dialog
          header="Confirm Status Change"
          visible={confirmStatusVisible}
          onHide={() => (confirmActionLoading ? null : setConfirmStatusVisible(false))}
          modal
          style={{ width: '520px' }}
        >
          <p>
            {nextStatusValue ? 'Enable' : 'Disable'} resource <strong>{selectedResource ? selectedResource.title : ''}</strong>?
          </p>
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmStatusVisible(false)} disabled={confirmActionLoading} />
            <Button label={nextStatusValue ? 'Enable' : 'Disable'} onClick={performStatusChange} disabled={confirmActionLoading} />
          </div>
        </Dialog>
      </Container>
    </Card>
  );
};

export default AdminResources;
